import { AuditLogEvent } from '../types';
import { formatDateOnly } from './datetime';

export type AuditFieldChange = {
  fieldKey: string;
  label: string;
  oldValue: string;
  newValue: string;
};

const EMPLOYEE_FIELD_LABELS: Record<string, string> = {
  FirstName: 'First name',
  LastName: 'Last name',
  Email: 'Email',
  Phone: 'Phone',
  Pin: 'PIN',
  IsActive: 'Active',
  Gender: 'Gender',
  GenderOther: 'Gender (other)',
  Birthday: 'Birthday',
  Address: 'Address',
  Address1: 'Address line 1',
  Address2: 'Address line 2',
  Address3: 'Address line 3',
  County: 'County',
  ZipCode: 'Postcode / ZIP',
  Title: 'Title',
  VisaNumber: 'Visa number',
  VisaExpiryDate: 'Visa expiry',
  MainLocationId: 'Main location',
  MainPositionId: 'Main position',
  PayrollDepartmentId: 'Payroll department',
  LineManagerEmployeeId: 'Line manager',
  IsManager: 'Employee or Manager',
  StartDate: 'Start date',
  RoleId: 'App role',
  AppRoles: 'App roles',
  SubLocations: 'Sub locations',
  SubPositions: 'Sub positions',
  Schedules: 'Schedules',
  WorksNumber: 'Works number',
  PpsNumber: 'PPS number',
  Ban: 'BAN',
  Bic: 'BIC',
  AccountNumber: 'Account number',
  SortCode: 'Sort code',
  AccountName: 'Account name',
  BankName: 'Bank name',
  PaymentType: 'Payment type',
  HourlyRate: 'Hourly rate',
  SundayRate: 'Sunday rate',
  BankHolidayRate: 'Bank holiday rate',
  IsPremiumPayEligible: 'Premium pay eligible',
  AllowAdditiveHourlyPay: 'Allow additive hourly / multi-role',
  IsPayExtra: 'Pay Extra',
  PayModelId: 'Pay model',
  PasswordChanged: 'Password',
  PasswordAnswerChanged: 'Password answer',
  PasswordQuestion: 'Password question',
  PasswordAnswer: 'Password answer',
  Attachments: 'Attachments',
  Cycles: 'Cycles',
  TrainingTypeId: 'Training type',
  DueDate: 'Due date',
  CompletionDate: 'Completion date',
  Status: 'Status',
  Type: 'Type',
  ActionTaken: 'Action taken',
  Description: 'Description',
  FollowupRecords: 'Follow-up records',
  Priority: 'Priority',
  LicenseType: 'License type',
  LicenseNumber: 'License number',
  ExpiryDate: 'Expiry date',
  NoteDate: 'Note date',
  Subject: 'Subject',
  Content: 'Content',
  LeaveTypeId: 'Leave type',
  LeaveYear: 'Leave year',
  EntitledDays: 'Entitled days',
  AccruedDays: 'Accrued days',
  TakenDays: 'Taken days',
  PendingDays: 'Pending days',
  CarriedForwardDays: 'Carried forward days',
  LastAccrualDate: 'Last accrual date',
  NextAccrualDate: 'Next accrual date',
  AllowMobileClockIn: 'Allow mobile clock-in',
  LeaveYearStartMonth: 'Leave year start month',
  ContractHoursPerWeek: 'Contract hours per week',
  ProfileImageUrl: 'Profile image',
  FaceId: 'Face ID',
  MustChangePassword: 'Must change password',
  PsaLicense: 'PSA license',
  EmployeeId: 'Employee',
  EmployeeProfileId: 'Profile ID',
  EmploymentStatus: 'Employment status',
  EmploymentEndDate: 'Employment end date',
  LeavePolicyId: 'Leave policy',
  Comment: 'Comment',
  DisplayOrder: 'Display order',
  EmploymentType: 'Employment type',
  PayrollEmploymentTypeId: 'Payroll employment type',
  NotesSubject: 'Notes subject',
  NotesContent: 'Notes content',
  IsApproved: 'Approved',
};

const PAYROLL_FIELD_LABELS: Record<string, string> = {
  FromDate: 'From date',
  ToDate: 'To date',
  EffectiveFrom: 'Effective from',
  IsActive: 'Active',
  BaseRate: 'Base rate',
  PaymentTypeId: 'Payment type',
  PaymentFrequencyId: 'Payment frequency',
  ContractedHours: 'Contracted hours',
  SundayRateMultiplier: 'Sunday rate multiplier',
  BankHolidayRateMultiplier: 'Bank holiday rate multiplier',
  NightShiftMultiplier: 'Night shift multiplier',
  AssignLaborCostById: 'Assign labor cost by',
  WorkingDaysPerWeek: 'Working days per week',
  DailyRate: 'Daily rate',
  OvertimeEligible: 'Overtime eligible',
  Ot1TriggerHours: 'OT1 trigger hours',
  Ot2TriggerHours: 'OT2 trigger hours',
  Ot1RateMultiplier: 'OT1 rate multiplier',
  Ot2RateMultiplier: 'OT2 rate multiplier',
  ProjectStartDate: 'Project start date',
  ProjectEndDate: 'Project end date',
  PayRecordId: 'Pay record',
  PayRate: 'Pay rate',
  PayRateMatrix: 'Pay rate matrix',
  WorkingDays: 'Working days',
  DisplayOrder: 'Display order',
  WorkingDayId: 'Working day',
  PayFrequency: 'Pay frequency',
  PayUnit: 'Pay unit',
  LeaveAccrualBasis: 'Leave accrual basis',
  TaxMethod: 'Tax method',
  PeriodStart: 'Period start',
  PeriodEnd: 'Period end',
  Status: 'Status',
  Notes: 'Notes',
  FinalizedAt: 'Finalized at',
  FinalizedByEmployeeId: 'Finalized by',
  PreviewFiltersJson: 'Preview filters',
  FinalizedPreviewJson: 'Finalized preview',
  LocationId: 'Location',
  ScheduleId: 'Schedule',
  PositionId: 'Position',
  Name: 'Name',
};

const SHIFT_FIELD_LABELS: Record<string, string> = {
  StartTime: 'Start time',
  EndTime: 'End time',
  PaidHours: 'Paid hours',
  UnpaidHours: 'Unpaid hours',
  BreakTimePaid: 'Break paid',
  BreakUseGlobalSettings: 'Use global break settings',
  IsManualColorOverride: 'Manual color override',
  DisplayOption: 'Display option',
  Color: 'Color',
  Notes: 'Notes',
  LocationId: 'Location',
  locationName: 'Location',
  ScheduleId: 'Schedule',
  scheduleName: 'Schedule',
  PositionId: 'Position',
  positionName: 'Position',
  employeeName: 'Employee',
  weekStartDate: 'Week start',
  importedCount: 'Imported count',
  failedCount: 'Failed count',
  skippedCount: 'Skipped count',
  deletedCount: 'Deleted count',
  importType: 'Import type',
  sourceWeekStartDate: 'Source week',
  employeeCount: 'Employees reordered',
  fileName: 'File name',
  batchId: 'Batch ID',
};

const SCHEDULE_FIELD_LABELS: Record<string, string> = {
  scheduleName: 'Schedule',
  locationName: 'Location',
  weekStartDate: 'Week start',
  status: 'Status',
  employeeCount: 'Employees reordered',
};

const AUDIT_FIELD_LABELS: Record<string, string> = {
  ...EMPLOYEE_FIELD_LABELS,
  ...PAYROLL_FIELD_LABELS,
  ...SHIFT_FIELD_LABELS,
  ...SCHEDULE_FIELD_LABELS,
};

const HIDDEN_AUDIT_KEYS = new Set([
  '__section',
  'Id',
  'EmployeeId',
  'EmployeeProfileId',
  'PayRecordId',
  'TrainingRecordId',
  'employeeName',
  'LocationId',
  'ScheduleId',
  'PositionId',
  'UpdatedAt',
  'CreatedAt',
  'Password',
  'PasswordHash',
  'PasswordAnswer',
  'PasswordResetToken',
  'PasswordResetTokenExpiry',
  'LastPasswordReAttemptDate',
]);

export function parseAuditJson(value: string | null | undefined): Record<string, unknown> | null {
  if (!value) return null;
  try {
    const parsed = JSON.parse(value);
    if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
      return parsed as Record<string, unknown>;
    }
  } catch {
    return null;
  }
  return null;
}

export function getAuditSectionLabel(log: AuditLogEvent): string | null {
  const field = log.fields?.[0];
  const fromNew = parseAuditJson(field?.newValue);
  const fromOld = parseAuditJson(field?.oldValue);
  const section = fromNew?.__section ?? fromOld?.__section;
  return section != null ? String(section) : null;
}

export function getAuditActionLabel(log: AuditLogEvent): string {
  return getAuditSectionLabel(log) || log.action;
}

export function getAuditSubjectName(log: AuditLogEvent): string {
  if (log.entityDisplay?.trim()) {
    return log.entityDisplay.trim();
  }

  const field = log.fields?.[0];
  const fromNew = parseAuditJson(field?.newValue);
  const fromOld = parseAuditJson(field?.oldValue);
  const blob = fromNew ?? fromOld;
  if (!blob) return '-';

  const first = String(blob.FirstName ?? blob.firstName ?? '').trim();
  const last = String(blob.LastName ?? blob.lastName ?? '').trim();
  const fullName = `${first} ${last}`.trim();
  if (fullName) return fullName;

  const employeeName = blob.employeeName ?? blob.EmployeeName;
  if (employeeName != null && String(employeeName).trim()) {
    return String(employeeName).trim();
  }

  const scheduleName = blob.scheduleName ?? blob.ScheduleName;
  const locationName = blob.locationName ?? blob.LocationName;
  if (scheduleName != null && locationName != null) {
    return `${String(scheduleName).trim()} · ${String(locationName).trim()}`;
  }

  if (scheduleName != null && String(scheduleName).trim()) {
    return String(scheduleName).trim();
  }

  return '-';
}

function normalizeAuditDisplayValue(value: unknown): string | null {
  if (value == null) return null;
  if (typeof value === 'boolean') return value ? 'Yes' : 'No';
  if (typeof value === 'number' && Number.isFinite(value)) {
    return String(value);
  }
  const text = String(value).trim();
  if (text.length === 0) return null;
  if (/^-?\d+(\.\d+)?$/.test(text)) {
    const numeric = Number(text);
    if (!Number.isNaN(numeric)) {
      return String(numeric);
    }
  }
  return text;
}

function formatFieldLabel(fieldKey: string): string {
  if (AUDIT_FIELD_LABELS[fieldKey]) {
    return AUDIT_FIELD_LABELS[fieldKey];
  }
  if (fieldKey.startsWith('Pay rate — ')) {
    return fieldKey;
  }
  return fieldKey
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .replace(/_/g, ' ')
    .trim();
}

function formatCycleListDisplay(value: string): string {
  return value.replace(/\b(\d{4}-\d{2}-\d{2})\b/g, (isoDate) => formatDateOnly(isoDate) || isoDate);
}

function formatDisplayValue(fieldKey: string, value: unknown): string {
  const normalized = normalizeAuditDisplayValue(value);
  if (normalized == null) return '(empty)';
  if (normalized === 'true') return 'Yes';
  if (normalized === 'false') return 'No';
  if (fieldKey === 'Cycles') {
    return formatCycleListDisplay(normalized);
  }
  if (/^\d{4}-\d{2}-\d{2}/.test(normalized)) {
    const formatted = formatDateOnly(normalized);
    if (formatted) return formatted;
  }
  return normalized;
}

/** Audit rows store field snapshots as JSON in a single "all" field. */
export function supportsFriendlyAuditFieldDiff(log: AuditLogEvent): boolean {
  if (
    log.entity !== 'Employees'
    && log.entity !== 'Payroll'
    && log.entity !== 'Shifts'
    && log.entity !== 'Schedules'
  ) {
    return false;
  }
  const field = log.fields?.[0];
  if (!field) return false;
  return parseAuditJson(field.oldValue) != null || parseAuditJson(field.newValue) != null;
}

/** @deprecated Use supportsFriendlyAuditFieldDiff */
export function supportsFriendlyEmployeesFieldDiff(log: AuditLogEvent): boolean {
  return supportsFriendlyAuditFieldDiff(log);
}

export function isEmployeeUpdatedLog(log: AuditLogEvent): boolean {
  return supportsFriendlyAuditFieldDiff(log) && getAuditSectionLabel(log) === 'Employee Updated';
}

export function getAuditFieldChanges(log: AuditLogEvent): AuditFieldChange[] {
  const field = log.fields?.[0];
  const oldBlob = parseAuditJson(field?.oldValue) ?? {};
  const newBlob = parseAuditJson(field?.newValue) ?? {};
  const keys = Array.from(new Set([
    ...Object.keys(oldBlob),
    ...Object.keys(newBlob),
  ]));

  const changes: AuditFieldChange[] = [];
  for (const key of keys) {
    if (HIDDEN_AUDIT_KEYS.has(key)) continue;

    const oldNormalized = normalizeAuditDisplayValue(oldBlob[key]);
    const newNormalized = normalizeAuditDisplayValue(newBlob[key]);
    if (oldNormalized === newNormalized) continue;

    changes.push({
      fieldKey: key,
      label: formatFieldLabel(key),
      oldValue: formatDisplayValue(key, oldBlob[key]),
      newValue: formatDisplayValue(key, newBlob[key]),
    });
  }

  return changes.sort((a, b) => a.label.localeCompare(b.label));
}

export function getEmployeesAuditFieldChanges(log: AuditLogEvent): AuditFieldChange[] {
  return getAuditFieldChanges(log);
}

/** @deprecated Use getAuditFieldChanges */
export const getEmployeeUpdatedFieldChanges = getEmployeesAuditFieldChanges;

/** @deprecated Use getAuditFieldChanges */
export function getPayrollAuditFieldChanges(log: AuditLogEvent): AuditFieldChange[] {
  return getAuditFieldChanges(log);
}

export function formatLogFieldsJson(log: AuditLogEvent): string {
  const field = log.fields?.[0];
  const tryParse = (value: string | null | undefined) => parseAuditJson(value) ?? value ?? null;

  const normalizedFields = (log.fields || []).map((item) => ({
    fieldKey: item.fieldKey,
    fieldLabel: item.fieldLabel,
    oldValue: tryParse(item.oldValue ?? null),
    newValue: tryParse(item.newValue ?? null),
  }));

  return JSON.stringify(normalizedFields, null, 2);
}
